import Auth from '../Auth/Auth';
import ls from '../lib/localStorage';
import pWaitFor from 'p-wait-for';
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import {socket} from "../lib/picos/socket";
import {globalAbortSignal} from "../lib/picos/abortController";

/**
 * Why an exec run failed (OWA-787, CXR-13-001). 'infrastructure': the socket
 * or authentication was not ready, and the lifecycle that readies it calls
 * start() again. 'data': the callbacks ran and failed or refused, which only
 * the scheduler retries.
 */
type ExecFailureKind = 'infrastructure' | 'data';

export enum PollingOptions {
    FULL = 'full',
    PARTIAL = 'partial'
}

export type PollingOptionTypes = 'full' | 'partial';

/**
 * Should we do excessive logging?
 */
let logging = ls.getBoolean(ls.QUEUE_LOGGING, false);

/**
 * The periodic background partial fetch (OWA-787; decision, John 2026-08-24).
 * Armed after a start() whose fetch ran, cleared by stop(). Without it there
 * is no recurring download: steady-state sync rests on socket pushes alone,
 * and a missed push stays missing until the next login, reconnect or manual
 * refresh.
 *
 * A chain of timeouts rather than an interval: the next run is scheduled only
 * after the current one finishes, so a slow fetch never has a second one
 * started behind it.
 */
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Cadence for the background refresh. Partial fetches ask only for rows newer
 * than each entity's maxDbAddedOn, so five minutes costs the server little and
 * repairs a missed socket push within five minutes.
 */
let refreshIntervalMs = 5 * 60 * 1000;

/**
 * Delay before the next run after a failed or deferred one (another tab owned
 * the sync cycle, a restore was still running, the fetch failed). Shorter than
 * the cadence so a first fetch that could not run is not left for five minutes.
 */
let retryIntervalMs = 30 * 1000;

/**
 * Timeout called every time something is saved to the queue. It is a 1 second timeout, which gives some time for
 * other changes to be added. This timeout will be canceled and reset every time more data is added to the queue.
 */
// let checkQueueTimeout: NodeJS.Timeout;

/**
 * Is the exec function currently running?
 *
 * @type {boolean}
 */
let isExecRunning: boolean = false;

/**
 * Is the start method running?
 *
 * @type {boolean}
 */
let inStart: boolean = false;

/**
 * Is pooling enabled?
 *
 * @type {boolean}
 */
let pollingEnabled: boolean = false;

/**
 * Array of callbacks to call when the scheduler runs.  Order of execution is not guaranteed.
 * @type {Array<Function>}
 */
let callbacks: Function[] = [];

const pollingFormat = "background: #FFD580; color: black;padding: 4px; border-radius: 3px;";
const pollingErrorFormat = "background: #FFD580; color: maroon;padding: 4px; border-radius: 3px;";

/**
 * Based on the last partial, are we doing a full or partial refresh?
 *
 * @returns {PollingOptionTypes}
 */
export function getExecType(): PollingOptionTypes {
    return ls.getString(ls.REFRESH_TYPE, 'full', true) as PollingOptionTypes;
}

/**
 * This function is called after a change is made to data that needs to be sent to the server.
 * We set a 1-second timeout, so if more data is changed in that time we reset the timer, essentially
 * waiting for one large group of data to send over.
 *
 * @param {function} [cb] - function to call when finished with queue processing (not currently used)
 */
// export function checkQueue(cb?: Function) {
//     // If the timer is set, reset it
//     if (checkQueueTimeout) {
//         if (logging) console.debug('%c⮃ checkQueue clearTimeout', pollingFormat);
//         clearTimeout(checkQueueTimeout);
//     }
//
//     if (logging) console.debug('%c⮃ checkQueue setTimeout', pollingFormat);
//     if (!!cb) checkQueueTimeout = setTimeout(exec, 1000, 'partial', 'checkQueue', cb);
// }

/**
 * Start polling.  Usually called when we get a connection to the server.
 *
 * @param {string} from
 */
export async function start(from: string) {
    if (pollingEnabled) {
        if (logging) console.debug(`%c⮃ Polling: start called from ${from}, but already polling`, pollingFormat);
        return;
    }
    else pollingEnabled = true;

    if (logging) console.debug(`%c⮃ Polling: start called from ${from}`, pollingFormat);

    // Mutex: if we are already in this method, return
    if (inStart) {
        if (logging) console.debug('%c⮃ Polling: Already in start', pollingFormat);
        return;
    }

    // Mutex-like functionality
    inStart = true;

    try {
        // Run immediately first, then wait
        if (logging) console.debug('%c⮃ Polling: initial call to exec after polling start', pollingFormat);

        let failureKind: ExecFailureKind | null = null;

        if (!isExecRunning) await exec(getExecType(), `${from} => start`, (status: any, kind: ExecFailureKind = 'infrastructure') => {
            if (status) {
                failureKind = kind;
                if (logging) console.debug(`%c⮃ Polling: Returned from exec with error ${status}`, pollingErrorFormat);
            }
            else if (logging) console.debug('%c⮃ Polling: Returned from exec successfully', pollingFormat);
        });

        // OWA-787: only an infrastructure bail (socket not connected,
        // authentication never completed) disables polling, because the
        // reconnect and login lifecycle calls start() again. A data failure
        // (the fetch refused, deferred, partly applied or threw while the
        // socket and authentication were fine) has no such trigger, so polling
        // stays enabled and the scheduler retries it. Disabling here left a
        // connected tab with no recurring download after one failure.
        if (failureKind === 'infrastructure') {
            pollingEnabled = false;
        }
        // stop() may have run while exec was awaited (logout, socket loss)
        else if (pollingEnabled) {
            armScheduler(failureKind === 'data' ? retryIntervalMs : refreshIntervalMs);
        }
    }
    finally {
        // Always ensure that we reset the start flag! If not, reconnection will not restart the queue
        inStart = false;
    }
}

/**
 * Is this tab in a state where a background fetch is useful? A hidden tab is
 * refreshed when it becomes visible again (App's visibility handler restarts
 * polling), and an offline browser cannot reach the server.
 */
function canRefreshNow(): boolean {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    return true;
}

/**
 * (Re)arm the background refresh. Each run schedules the next one only after
 * it finishes: the normal cadence after a fetch that applied, the shorter
 * retry delay after one that failed or deferred. A run that finds the tab
 * hidden or offline, or another fetch already running, fetches nothing and
 * checks again at the normal cadence.
 * Socket pushes still carry most changes; this is the repair path for the
 * ones that were missed.
 *
 * @param {number} delayMs - delay before the next run
 */
function armScheduler(delayMs: number) {
    if (refreshTimer) clearTimeout(refreshTimer);

    refreshTimer = setTimeout(async () => {
        refreshTimer = undefined;
        if (!pollingEnabled) return;

        let next = refreshIntervalMs;
        try {
            if (!canRefreshNow()) {
                if (logging) console.debug('%c⮃ Polling: scheduled refresh skipped, tab hidden or offline', pollingFormat);
            }
            else if (isExecRunning) {
                // Another run (a socket reconnect, a manual refresh) is fetching now
                if (logging) console.debug('%c⮃ Polling: scheduled refresh skipped, exec already running', pollingFormat);
            }
            else {
                await exec(getExecType(), 'scheduler', (status: any) => {
                    if (status) {
                        next = retryIntervalMs;
                        console.warn(`%c⮃ Polling: scheduled refresh did not complete: ${status}`, pollingErrorFormat);
                    }
                    else if (logging) console.debug('%c⮃ Polling: scheduled refresh completed', pollingFormat);
                });
            }
        }
        catch (err) {
            next = retryIntervalMs;
            console.error('%c⮃ Polling: scheduled refresh error', pollingErrorFormat, err);
        }

        // stop() during the run clears pollingEnabled; a new start() arms its own timer
        if (pollingEnabled && !refreshTimer) armScheduler(next);
    }, delayMs);
}

/**
 * Stop polling.  Usually called when we lose the connection to the server or log out.
 *
 * Clears the timeout and sets run to false.
 *
 * @param {string} from
 */
export function stop(from: string) {
    // The scheduler lives and dies with polling (OWA-787)
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = undefined;
    }

    if (pollingEnabled) {
        pollingEnabled = false;

        if (logging) console.debug(`%c⮃ Polling: stopped called from ${from}`, pollingFormat);
    }
    else {
        if (logging) console.debug(`%c⮃ Polling: stopped called from ${from}, but already stopped`, pollingFormat);
    }
}

/**
 * Call full refresh externally (Support, change of firm, etc)
 * @param {string} requesterName - who is requesting?
 * @param {function} [callback] - function to call when finished
 */
export function doFullRefresh(requesterName: string, callback?: Function) {
    // Set the last partial time to 0, forcing a full refresh
    ls.setString(ls.REFRESH_TYPE, 'full', true);

    if (logging) console.debug('%c⮃ Polling: doFullRefresh', pollingFormat, pollingFormat, requesterName);

    const doExec = () => exec('full', `${requesterName} => doFullRefresh`, (status: string) => {
        if (logging) {
            if (status) console.debug(`%c⮃ Returned from exec with error ${status}`, pollingErrorFormat);
            else console.debug('%c⮃ Polling: Returned from exec successfully', pollingFormat);
        }

        if (typeof (callback) === 'function') callback();
    }).catch(err => console.error(`%c⮃ Polling: error running exec(): ${err?.message}`, pollingErrorFormat, err));

    // If exec is not running, we run it now, forcing the full refresh
    if (!isExecRunning) doExec().catch(err => console.error('%c⮃ Polling', pollingErrorFormat, err));
    else pWaitFor(() => !isExecRunning, {interval: 1000, signal: globalAbortSignal})
        .then(() => doExec())
        .catch(err => console.error('%c⮃ Polling', pollingErrorFormat, err));
}

/**
 * Call full refresh externally (Support, change of firm, etc)
 * @param {string} requesterName - who is requesting?
 * @param {Function} [callback] - function to call when finished
 */
export function doPartialRefresh(requesterName: string, callback?: Function) {
    if (logging) console.debug('%c⮃ Polling: doPartialRefresh', pollingFormat, requesterName, ls.getString(ls.REFRESH_TYPE, 'full', true));

    // If exec is not running, we run it now, forcing the partial refresh
    if (!isExecRunning) {
        exec('partial', `${requesterName} => doPartialRefresh`, (status: any) => {
            if (logging) {
                if (status) console.debug(`%c⮃ Polling: Returned from exec with error ${status}`, pollingErrorFormat);
                else console.debug('%c⮃ Returned from exec successfully', pollingFormat);
            }

            if (typeof (callback) === 'function') callback();
        }).catch(err => console.error(`%c⮃ Polling: error running exec(): ${err?.message}`, pollingErrorFormat, err));
    }
    else {
        console.debug('%c⮃ Polling: doPartialRefresh called while exec running', pollingFormat);
    }
}

/**
 * Add a callback to the scheduler
 * @param {string} from
 * @param {Function} callback
 */
export function addCallback(from: string, callback: Function) {
    if (logging) console.debug(`⮃ Polling: addCallback called from ${from}`, pollingFormat);

    if (callbacks.indexOf(callback) === -1)
        callbacks.push(callback);

    if (logging) console.debug(`%c⮃ Polling: Added polling listener, listener count is now ${callbacks.length}`, pollingFormat);
}

/**
 * Remove a callback from the scheduler
 * @param {Function} callback
 */
export function removeCallback(callback: Function) {
    callbacks = callbacks.filter(cb => callback !== cb);
}

/**
 * Complete rewrite of this functionality.  We used to use algorithms to figure out when to call processQueue, and
 * when to do a full refresh.  We have greatly simplified this now:
 * - processQueue is now called from redux when something has changed, indicating that we need to send data to the server
 * - full refreshes have been replaced with scheduled partial refreshes, which are called by an interval handler.
 * - a full refresh only happens when outlaw.fetch.lastFetch is set to 0.
 * - only one callback is ever called: processQueue.
 *
 * Call each of the callbacks in the callback array, one at a time.  The only one right now is from App: processQueue.
 * Pass an options object that contains the fullRefresh flag.
 *
 * @param {PollingOptionTypes} type - what kind of execution are we doing, full or partial?
 * @param {string} from - who called us?
 * @param {Function} [callback] - function to call when work is done
 */
async function exec(type: PollingOptionTypes, from: string, callback: Function) {
    if (logging) console.debug('%c⮃ Polling: exec', pollingFormat, from);
    if (logging) if (isExecRunning) console.debug(`%c⮃ exec() called while already running`, pollingFormat);

    isExecRunning = true;

    try {
        // Ensure the socket is connected. Wait for the connection before bailing.
        if (!socket?.connected) {
            try {
                await pWaitFor(() => !!socket?.connected, {timeout: waitTimeoutMs, signal: globalAbortSignal});
            }
            catch (err) {
                if (logging) console.warn('%c⮃ Polling: exec called when not connected', pollingFormat);
                if (typeof (callback) === 'function') callback('exec called when not connected', 'infrastructure');
                return;
            }
        }

        // Ensure the user is authenticated. Wait before bailing.
        if (!Auth.isAuthenticated() && getCurrentFirmId() && getCurrentUserId()) {
            try {
                if (logging) console.debug('%c⮃ Polling: waiting for user to authenticate', pollingFormat);

                await pWaitFor(() => Auth.isAuthenticated() && !!getCurrentFirmId() && !!getCurrentUserId(),
                    {timeout: waitTimeoutMs, interval: 200, signal: globalAbortSignal}
                );

                if (logging) console.debug('%c⮃ Polling: user authenticated', pollingFormat);
            }
            catch (err) {
                // OWA-787: this bail used to return without its callback, so
                // start() recorded success with nothing fetched and every later
                // start() was a no-op. A slow login left a session that never
                // made its first fetch.
                if (logging) console.warn('%c⮃ Polling: exec timed out waiting for the user to authenticate', pollingFormat);
                if (typeof (callback) === 'function') callback('exec timed out waiting for authentication', 'infrastructure');
                return;
            }
        }

        // Call the callback with an options object
        const options = {type};
        const promises: Promise<{ok: boolean}>[] = [];

        // Each callback's result is read (OWA-787). A callback that rejects,
        // or resolves false (the fetch was refused, deferred or not fully
        // applied), is a failed run. The results used to go into an array
        // nothing read, so a refused fetch was reported as success.
        // The only callback is fetchFromServer (added by App and Auth).
        for (let i = 0; i < callbacks.length; i++) {
            if (logging) console.debug(`⮃ Polling: exec calling callback #${i + 1}`, pollingFormat);
            promises[i] = Promise.resolve(callbacks[i](options))
                .then((res: any) => ({ok: res !== false}))
                .catch((err: any) => {
                    console.error('%c⮃ Polling: exec callback failed', pollingErrorFormat, err);
                    return {ok: false};
                });
        }

        const outcomes = await Promise.all(promises);
        const failed = outcomes.filter(o => !o.ok).length;

        if (typeof (callback) === 'function') {
            if (failed > 0) {
                if (logging) console.debug(`%c⮃ Polling: exec had ${failed} failed callback(s)`, pollingErrorFormat);
                callback(`${failed} polling callback(s) failed or refused`, 'data');
            }
            else {
                if (logging) console.debug('%c⮃ Polling: exec called all callbacks successfully', pollingFormat);
                callback();
            }
        }
    }
    catch (err: any) {
        console.error(`%c⮃ Polling`, pollingErrorFormat, err);

        if (callback) callback(err.message, 'data');
    }
    finally {
        if (logging) console.debug('%c⮃ Polling: exec finished', pollingFormat);

        isExecRunning = false;
    }
}

/**
 * How long exec waits for the socket and for authentication before bailing.
 * p-wait-for binds setTimeout at import, so fake timers cannot drive these
 * waits in tests; the test seam below shrinks them instead.
 */
let waitTimeoutMs = 5_000;

/**
 * Test-only seam. PROD-stripped.
 */
export const __test__ = import.meta.env.PROD ? undefined : {
    setWaitTimeoutMs(ms: number) {
        waitTimeoutMs = ms;
    },
    setRefreshIntervalMs(ms: number) {
        refreshIntervalMs = ms;
    },
    setRetryIntervalMs(ms: number) {
        retryIntervalMs = ms;
    },
    isExecRunning() {
        return isExecRunning;
    },
};
